import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Chip,
    Select,
    MenuItem,
    FormControl,
    Snackbar,
    Alert
} from '@mui/material';
import axios from '../../../api/axios';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

// Weekday numbering follows Python's date.weekday(): Monday=0 .. Sunday=6.
// Friday (4) is a closed service day and cannot be scheduled.
const FRIDAY = 4;
const WEEKDAY_ROWS = [
    { weekday: 5, name: 'Saturday' },
    { weekday: 6, name: 'Sunday' },
    { weekday: 0, name: 'Monday' },
    { weekday: 1, name: 'Tuesday' },
    { weekday: 2, name: 'Wednesday' },
    { weekday: 3, name: 'Thursday' },
    { weekday: FRIDAY, name: 'Friday' }
];

const emptyDraft = () => {
    const draft = {};
    WEEKDAY_ROWS.forEach(({ weekday }) => {
        if (weekday !== FRIDAY) draft[weekday] = '';
    });
    return draft;
};

// Dialog to manage a user's weekday -> delivery address schedule.
// Saving with at least one weekday set pins every other working weekday to
// the current default address (backend full-week materialization); clearing
// removes the user from automatic switching entirely.
function AddressSchedulerDialog({ open, onClose, userId, userName }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [draft, setDraft] = useState(emptyDraft());
    const [hasStoredSchedule, setHasStoredSchedule] = useState(false);
    const [saving, setSaving] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const applyScheduleResponse = (schedule) => {
        const next = emptyDraft();
        let anySet = false;
        (schedule || []).forEach(entry => {
            if (entry.weekday !== FRIDAY && entry.address) {
                next[entry.weekday] = entry.address.id;
                anySet = true;
            }
        });
        setDraft(next);
        setHasStoredSchedule(anySet);
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!open || !userId) return;
            setLoading(true);
            setError(null);
            try {
                const [scheduleRes, addressesRes] = await Promise.all([
                    axios.get('user/address/schedule/', { params: { user_id: userId } }),
                    axios.get('user/address/list/', { params: { user_id: userId } })
                ]);
                setAddresses(Array.isArray(addressesRes.data?.addresses) ? addressesRes.data.addresses : []);
                applyScheduleResponse(scheduleRes.data?.schedule);
            } catch (e) {
                console.error('Failed to load address schedule', e);
                setError('Failed to load address schedule');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [open, userId]);

    const handleDayChange = (weekday) => (e) => {
        setDraft(prev => ({ ...prev, [weekday]: e.target.value }));
    };

    const handleSave = async () => {
        const schedule = {};
        Object.entries(draft).forEach(([weekday, addressId]) => {
            if (addressId !== '') schedule[weekday] = addressId;
        });
        if (Object.keys(schedule).length === 0) {
            setSnackbar({ open: true, message: 'Set at least one day, or use Clear Schedule', severity: 'info' });
            return;
        }
        setSaving(true);
        try {
            const res = await axios.post('user/address/schedule/set/', { user_id: userId, schedule });
            applyScheduleResponse(res.data?.schedule);
            setSnackbar({ open: true, message: 'Schedule saved', severity: 'success' });
        } catch (err) {
            console.error('Failed to save address schedule', err);
            const message = err.response?.data?.error || 'Failed to save schedule';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleClear = async () => {
        setClearing(true);
        try {
            await axios.post('user/address/schedule/set/', { user_id: userId, schedule: {} });
            setDraft(emptyDraft());
            setHasStoredSchedule(false);
            setSnackbar({ open: true, message: 'Schedule cleared — deliveries follow the default address', severity: 'success' });
        } catch (err) {
            console.error('Failed to clear address schedule', err);
            const message = err.response?.data?.error || 'Failed to clear schedule';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setClearing(false);
        }
    };

    const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

    const addressLabel = (addr) => {
        const parts = [addr.area_name, addr.block ? `block ${addr.block}` : null].filter(Boolean);
        return parts.length > 0 ? `${addr.name || 'Unnamed'} — ${parts.join(', ')}` : (addr.name || 'Unnamed');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h6">Address Scheduler - {userName}</Typography>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error" variant="body2">{error}</Typography>
                ) : addresses.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No addresses found. Create addresses first from the Addresses dialog.
                    </Typography>
                ) : (
                    <>
                        <Alert severity="info" sx={{ mb: 2, fontSize: '0.75rem' }}>
                            Deliveries automatically use the address set for each weekday. On save, any
                            day left unset is pinned to the current default address. Clear Schedule
                            removes automation — deliveries follow the default address again.
                        </Alert>
                        {WEEKDAY_ROWS.map(({ weekday, name }) => (
                            <Box
                                key={weekday}
                                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}
                            >
                                <Typography variant="body2" sx={{ width: 90, fontWeight: 500 }}>
                                    {name}
                                </Typography>
                                {weekday === FRIDAY ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        Closed — no delivery
                                    </Typography>
                                ) : (
                                    <FormControl size="small" sx={{ flex: 1 }}>
                                        <Select
                                            value={draft[weekday]}
                                            onChange={handleDayChange(weekday)}
                                            displayEmpty
                                            renderValue={(value) => {
                                                if (value === '') {
                                                    return <Typography variant="body2" color="text.secondary">— Not set —</Typography>;
                                                }
                                                const addr = addresses.find(a => a.id === value);
                                                return addr ? addressLabel(addr) : `Address #${value}`;
                                            }}
                                        >
                                            <MenuItem value="">
                                                <Typography variant="body2" color="text.secondary">— Not set —</Typography>
                                            </MenuItem>
                                            {addresses.map(addr => (
                                                <MenuItem key={addr.id} value={addr.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                        <Typography variant="body2">{addressLabel(addr)}</Typography>
                                                        {addr.is_default && (
                                                            <Chip
                                                                icon={<LocalShippingIcon sx={{ fontSize: '0.9rem !important' }} />}
                                                                label="Default"
                                                                size="small"
                                                                color="success"
                                                                sx={{
                                                                    height: 20,
                                                                    pl: 0.6,
                                                                    '& .MuiChip-icon': { ml: 0, mr: 0.4 },
                                                                    fontSize: '0.65rem'
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>
                        ))}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {hasStoredSchedule && (
                    <Button
                        onClick={handleClear}
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={clearing || saving || loading}
                        sx={{ mr: 'auto' }}
                    >
                        {clearing ? 'Clearing…' : 'Clear Schedule'}
                    </Button>
                )}
                <Button onClick={onClose} size="small">Close</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={saving || clearing || loading || !!error || addresses.length === 0}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}

export default AddressSchedulerDialog;
